// 1. React
import React, { useState, useEffect, useCallback } from 'react';

// 2. Third-party
import { Table, Button, Input, Space, Popconfirm, Empty, Tooltip, Typography, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

// 3. Components
import Box from '@iso/components/utility/box';

// 4. Services
import { categoryGlobalFormService, subCategoryGlobalFormService } from '@iso/services';

// 5. Local components
import CategoryModal from './CategoryModal';

// 6. Styles
import { FormCategoriesWrapper } from './ManageLibraryForms.styles';

const SUCCESS_CODE = '0000';
const { Text } = Typography;

// The reserved catch-all category. It is the JSON-import fallback (IMP-02) and
// must always exist, so it cannot be deleted or renamed. The backend is the
// real guard (CategoryGlobalFormController); this only reflects it in the UI.
const PROTECTED_CATEGORY = 'other';
const isProtectedCategory = (record) =>
  record && String(record.name || '').trim().toLowerCase() === PROTECTED_CATEGORY;

const surfaceError = (res, fallback) => {
  if (res && res.errors && res.errors.name && res.errors.name.length) {
    message.error(res.errors.name[0]);
  } else {
    message.error((res && res.message) || fallback);
  }
};

// Per-category sub-category editor rendered in the expanded row.
const SubCategoryManager = ({ category, onChanged }) => {
  const [newName, setNewName] = useState('');
  const subs = category.subCategories || [];

  const add = () => {
    if (!newName.trim()) {
      return;
    }
    subCategoryGlobalFormService
      .storeOrUpdate({ sub_category_global_form: { category_global_form_id: category.id, name: newName.trim() } })
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          message.success('Sub-category added');
          setNewName('');
          onChanged();
        } else {
          surfaceError(res, 'Failed to add sub-category');
        }
      });
  };

  const rename = (id, name, previous) => {
    if (!name || !name.trim() || name === previous) {
      return;
    }
    subCategoryGlobalFormService.storeOrUpdate({ sub_category_global_form: { name } }, id).then((res) => {
      if (res && res.code === SUCCESS_CODE) {
        message.success('Sub-category renamed');
        onChanged();
      } else {
        surfaceError(res, 'Failed to rename sub-category');
      }
    });
  };

  const remove = (id) => {
    subCategoryGlobalFormService.destroy(id).then((res) => {
      if (res && res.code === SUCCESS_CODE) {
        message.success(`Deleted. ${res.affected_forms || 0} form(s) unlinked.`);
        onChanged();
      } else {
        message.error((res && res.message) || 'Failed to delete sub-category');
      }
    });
  };

  return (
    <div>
      <Text strong>Sub-categories</Text>
      <div style={{ marginTop: 8 }}>
        {subs.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No sub-categories" />}
        {subs.map((sub) => (
          <Space key={sub.id} style={{ display: 'flex', marginBottom: 8 }}>
            <Input
              defaultValue={sub.name}
              style={{ width: 260 }}
              onPressEnter={(e) => rename(sub.id, e.target.value, sub.name)}
              onBlur={(e) => rename(sub.id, e.target.value, sub.name)}
            />
            <Popconfirm
              title="Delete this sub-category? Linked forms will be unlinked."
              onConfirm={() => remove(sub.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ))}
        <Space style={{ marginTop: 4 }}>
          <Input
            value={newName}
            style={{ width: 260 }}
            placeholder="New sub-category name"
            onChange={(e) => setNewName(e.target.value)}
            onPressEnter={add}
          />
          <Button type="dashed" icon={<PlusOutlined />} onClick={add}>
            Add
          </Button>
        </Space>
      </div>
    </div>
  );
};

const FormCategoriesTab = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedKeys, setExpandedKeys] = useState([]);

  const fetchCategories = useCallback(() => {
    setLoading(true);
    categoryGlobalFormService
      .get()
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          setCategories(res.category_global_form || []);
        } else {
          message.error((res && res.message) || 'Failed to load categories');
        }
      })
      .catch((error) => {
        console.error('Load categories error:', error);
        message.error('Failed to load categories');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Client-side search over category name OR any of its sub-category names.
  const term = search.trim().toLowerCase();
  const filteredCategories = !term
    ? categories
    : categories.filter(
        (c) =>
          (c.name || '').toLowerCase().indexOf(term) !== -1 ||
          (c.subCategories || []).some((s) => (s.name || '').toLowerCase().indexOf(term) !== -1)
      );

  // Auto-expand rows whose sub-category matches, so the hit is visible.
  useEffect(() => {
    const t = search.trim().toLowerCase();
    if (!t) {
      setExpandedKeys([]);
      return;
    }
    setExpandedKeys(
      categories
        .filter((c) => (c.subCategories || []).some((s) => (s.name || '').toLowerCase().indexOf(t) !== -1))
        .map((c) => c.id)
    );
  }, [search, categories]);

  const handleAdd = () => {
    setEditingCategory(null);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCategory(record);
    setModalVisible(true);
  };

  const handleDelete = (id) => {
    categoryGlobalFormService.destroy(id).then((res) => {
      if (res && res.code === SUCCESS_CODE) {
        message.success('Category deleted');
        fetchCategories();
      } else {
        message.error((res && res.message) || 'Failed to delete category');
      }
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    { title: 'Description', dataIndex: 'description', key: 'description', render: (value) => value || '-' },
    {
      title: 'Sub-categories',
      key: 'subCount',
      width: 130,
      render: (_, record) => (record.subCategories ? record.subCategories.length : 0),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      defaultSortOrder: 'descend',
      sorter: (a, b) => (a.created_at || '').localeCompare(b.created_at || ''),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY HH:mm') : '-'),
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, record) => {
        if (isProtectedCategory(record)) {
          // "Other" is required as the import fallback — no rename, no delete.
          return (
            <Tooltip title="The “Other” category is required as the import fallback and cannot be renamed or deleted.">
              <Button type="link" icon={<EditOutlined />} disabled />
              <Button type="link" danger icon={<DeleteOutlined />} disabled />
            </Tooltip>
          );
        }
        return (
          <Space>
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
            <Popconfirm
              title="Delete this category? Linked forms will be unlinked."
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <FormCategoriesWrapper>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <Input.Search
          allowClear
          enterButton
          placeholder="Search category or sub-category"
          style={{ maxWidth: 320 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(value) => setSearch(value)}
        />
        <Button type="primary" className="btn-success" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Category
        </Button>
      </div>

      <Box id="categoriesBox">
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={filteredCategories}
          columns={columns}
          pagination={false}
          expandable={{
            expandedRowRender: (record) => <SubCategoryManager category={record} onChanged={fetchCategories} />,
            expandedRowKeys: expandedKeys,
            onExpandedRowsChange: (keys) => setExpandedKeys(keys),
          }}
        />
      </Box>

      <CategoryModal
        visible={modalVisible}
        category={editingCategory}
        onCancel={() => setModalVisible(false)}
        onSaved={fetchCategories}
      />
    </FormCategoriesWrapper>
  );
};

export default FormCategoriesTab;
